import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/** Parse Prometheus text format into a simple lookup */
function parseMetrics(text: string): Record<string, number> {
    const result: Record<string, number> = {};
    for (const line of text.split("\n")) {
        if (line.startsWith("#") || !line.trim()) continue;
        const match = line.match(/^([^\s{]+)(\{[^}]*\})?\s+(.+)$/);
        if (match) {
            const key = match[1] + (match[2] || "");
            result[key] = parseFloat(match[3]);
        }
    }
    return result;
}

function fmt(n: number | undefined, suffix = ""): string {
    if (n === undefined || isNaN(n)) return "-";
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + " GB" + suffix;
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(0) + " MB" + suffix;
    if (n >= 1_000) return (n / 1_000).toFixed(0) + " KB" + suffix;
    return n.toFixed(0) + suffix;
}

function fmtMs(n: number | undefined): string {
    if (n === undefined || isNaN(n)) return "-";
    if (n >= 1000) return (n / 1000).toFixed(1) + "s";
    return Math.round(n) + "ms";
}

function fmtTime(seconds: number | undefined): string {
    if (!seconds || isNaN(seconds)) return "-";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

export function MetricsBar() {
    const { data: raw } = useQuery({
        queryKey: ["metrics"],
        queryFn: api.metrics,
        staleTime: 1_000,
        refetchInterval: 2_000,
    });

    if (!raw) return null;
    const m = parseMetrics(raw);

    const chunksStored = m['download_chunk_count{status="stored"}'] || 0;
    const totalBytes = m["download_chunk_bytes"] || 0;
    const fetchP50 = m['download_chunk_latency_ms{phase="fetch",quantile="0.5"}'];
    const fetchP95 = m['download_chunk_latency_ms{phase="fetch",quantile="0.95"}'];
    const s3P50 = m['download_s3_latency_ms{quantile="0.5"}'];
    const s3P95 = m['download_s3_latency_ms{quantile="0.95"}'];
    const uptime = m["process_uptime_seconds"] || 0;
    const heap = m["process_heap_bytes"] || 0;
    const sqlSelect = m['sqlite_query_count{verb="SELECT"}'] || 0;
    const sqlInsert = m['sqlite_query_count{verb="INSERT"}'] || 0;
    const sqlP99 = m['sqlite_query_latency_ms{verb="SELECT",quantile="0.99"}'];
    const prepStmts = m["sqlite_prepared"] || 0;

    const throughput = uptime > 0 ? totalBytes / uptime : 0;

    // Collect error counts by status code
    const errors: Record<string, number> = {};
    let totalErrors = 0;
    for (const [key, val] of Object.entries(m)) {
        const match = key.match(/^download_chunk_errors\{status="(\w+)"\}$/);
        if (match && val > 0) {
            errors[match[1]] = val;
            totalErrors += val;
        }
    }

    return (
        <div style={{
            display: "flex", gap: 16, flexWrap: "wrap", padding: "8px 12px",
            background: "#111", borderRadius: 6, marginBottom: 16, fontSize: "0.78em", color: "#888",
            border: "1px solid #222",
        }}>
            <Metric label="chunks" value={chunksStored.toLocaleString()} />
            <Metric label="stored" value={fmt(totalBytes)} />
            <Metric label="throughput" value={fmt(throughput, "/s")} />
            <Sep />
            <Metric label="cdn p50" value={fmtMs(fetchP50)} color={fetchP50 > 500 ? "#aa8833" : "#33aa55"} />
            <Metric label="cdn p95" value={fmtMs(fetchP95)} color={fetchP95 > 1000 ? "#aa3333" : "#aa8833"} />
            <Metric label="s3 p50" value={fmtMs(s3P50)} color={s3P50 > 1000 ? "#aa8833" : "#33aa55"} />
            <Metric label="s3 p95" value={fmtMs(s3P95)} color={s3P95 > 2000 ? "#aa3333" : "#aa8833"} />
            <Sep />
            <Metric label="sql q" value={(sqlSelect + sqlInsert).toLocaleString()} />
            <Metric label="sql p99" value={fmtMs(sqlP99)} />
            <Metric label="stmts" value={String(prepStmts)} />
            <Metric label="heap" value={fmt(heap)} />
            <Metric label="uptime" value={fmtTime(uptime)} />
            {totalErrors > 0 && <>
                <Sep />
                <Metric label="errors" value={String(totalErrors)} color="#aa3333" />
                {Object.entries(errors).map(([code, count]) => (
                    <Metric key={code} label={code} value={String(count)} color={code === "network" || code === "s3" ? "#aa3333" : "#aa8833"} />
                ))}
            </>}
        </div>
    );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 50 }}>
            <span style={{ fontWeight: 600, color: color || "#e0e0e0", fontVariantNumeric: "tabular-nums", fontSize: "1.05em" }}>{value}</span>
            <span style={{ fontSize: "0.85em", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
        </div>
    );
}

function Sep() {
    return <div style={{ width: 1, background: "#333", alignSelf: "stretch" }} />;
}
